import React from "react";
import styles from "./admin.module.scss";
import cx from "./cx";

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T, index: number) => React.ReactNode;
  /** Use tabular numbers and no wrapping. */
  numeric?: boolean;
  width?: number | string;
  align?: "left" | "right" | "center";
}

interface TableProps<T> {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T, index: number) => React.Key;
  compact?: boolean;
  /** Highlight (mint) or dim a row. */
  rowState?: (row: T, index: number) => "on" | "dim" | undefined;
  onRowClick?: (row: T, index: number) => void;
  /** Rendered instead of the table body when there are no rows. */
  empty?: React.ReactNode;
  caption?: string;
  className?: string;
  /**
   * Extra content under a row (e.g. an expanded breakdown), rendered as a
   * full-width row. Return null for no extra row.
   */
  renderExpanded?: (row: T, index: number) => React.ReactNode | null;
}

function Table<T>({
  columns,
  rows,
  rowKey,
  compact,
  rowState,
  onRowClick,
  empty,
  caption,
  className,
  renderExpanded,
}: TableProps<T>) {
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className={styles.tblWrap}>
      <table
        className={cx(styles.tbl, compact && styles.tblCompact, className)}
      >
        {caption && <caption className={styles.srOnly}>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={{ width: c.width, textAlign: c.align }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const state = rowState?.(row, i);
            const expanded = renderExpanded?.(row, i);
            return (
              <React.Fragment key={rowKey(row, i)}>
                <tr
                  className={cx(
                    state === "on" && styles.rowOn,
                    state === "dim" && styles.rowDim,
                    onRowClick && styles.rowClickable,
                  )}
                  onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cx(c.numeric && styles.num)}
                      style={{
                        textAlign: c.align,
                        whiteSpace: c.numeric ? "nowrap" : undefined,
                      }}
                    >
                      {c.render(row, i)}
                    </td>
                  ))}
                </tr>
                {expanded != null && expanded !== false && (
                  <tr className={styles.rowExpanded}>
                    <td colSpan={columns.length}>{expanded}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
